import classNames from 'classnames'
import { format } from 'date-fns'
import { Info, Loader2 } from 'lucide-react'
import { Button, Icon, SelectField, Typography } from 'src/modules/core/components'
import { BookingAvailabilityError } from '../booking-availability-error'
import { CALENDAR_REFUSED_TITLE } from './booking-date-and-time-step.domain'
import type { BookingDateAndTimeStepProps, CalendarDayView } from './booking-date-and-time-step.interface'
import classes from './booking-date-and-time-step.module.scss'
import { useBookingDateAndTimeStep } from './use-booking-date-and-time-step'

export function BookingDateAndTimeStep(props: BookingDateAndTimeStepProps) {
  const f = useBookingDateAndTimeStep(props)

  return (
    <div className={classes.root}>
      <div className={classNames(classes.content, { [classes.contentSingle]: f.isCalendarRefused })}>
        <div className={classes.leftPanel}>
          <div className={classes.panelHeader}>
            <Icon name="calendarAlt" className={classes.panelIcon} />
            <Typography variant="h2" className={classes.panelTitle}>
              Select a Date
            </Typography>
          </div>
          <Typography variant="body" className={classes.panelDescription}>
            Choose when you&apos;d like to schedule your appointment
          </Typography>

          <div className={classes.formSection}>
            <Typography variant="body" className={classes.label}>
              Appointment Type
            </Typography>
            {f.showAppointmentTypeSelector ? (
              f.isLoadingAppointmentTypes ? (
                <Typography variant="body">Loading appointment types...</Typography>
              ) : f.appointmentTypeOptions.length === 0 ? (
                <Typography variant="body">No appointment types available.</Typography>
              ) : (
                <SelectField
                  value={f.appointmentTypeOptions.find((o) => o.value === f.selectedAppointmentType)}
                  onChange={(option: { value?: string }) => f.setSelectedAppointmentType(option?.value ?? '')}
                  options={f.appointmentTypeOptions}
                  closeMenuOnSelect={true}
                  placeholder="Select an appointment type"
                />
              )
            ) : (
              <Typography variant="body">{f.appointmentTypeLabelForDisplay}</Typography>
            )}
          </div>

          {f.cancellationPolicy && (
            <div className={classes.policyBox} data-testid="booking-cancellation-policy">
              <Info size={20} className={classes.policyIcon} aria-hidden="true" />
              <div className={classes.policyContent}>
                <Typography variant="body" className={classes.policyTitle}>
                  Cancellation Policy
                </Typography>
                <Typography variant="body" className={classes.policyText}>
                  {f.cancellationPolicy.text}
                </Typography>
              </div>
            </div>
          )}

          {f.hasSelectedAppointmentType ? (
            <div className={classes.formSection}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="body" className={classes.label}>
                  Available Dates
                </Typography>
                {f.isLoadingFirstDate && <Loader2 className={classes.spinner} size={16} aria-label="Loading" />}
              </div>
              {f.isCalendarRefused ? (
                <BookingAvailabilityError title={CALENDAR_REFUSED_TITLE} message={f.calendarRefusedMessage} />
              ) : (
                <>
                  {f.firstAvailableDateError && (
                    <BookingAvailabilityError
                      message={f.firstAvailableDateErrorMessage}
                      onRetry={f.handleRetryFirstAvailableDate}
                      isRetrying={f.isRetryingFirstAvailableDate}
                      className={classes.availabilityError}
                    />
                  )}
                  <div className={classes.calendar}>
                    <div className={classes.calendarHeader}>
                      <button
                        type="button"
                        onClick={f.handlePrevMonth}
                        className={classes.calendarNav}
                        disabled={!f.canNavigateToPrevMonth}
                      >
                        &lt;
                      </button>
                      <Typography variant="body" className={classes.calendarMonth}>
                        {format(f.currentMonth, 'MMMM yyyy')}
                      </Typography>
                      <button
                        type="button"
                        onClick={f.handleNextMonth}
                        className={classes.calendarNav}
                        disabled={!f.canNavigateToNextMonth}
                      >
                        &gt;
                      </button>
                    </div>
                    <div className={classes.calendarWeekdays}>
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                        <div key={day} className={classes.calendarWeekday}>
                          <Typography variant="body">{day}</Typography>
                        </div>
                      ))}
                    </div>
                    <div className={classes.calendarDays}>
                      {f.calendarDayViews.map((day) => {
                        if (!day.date) {
                          return <div key={day.key} className={classes.calendarDayEmpty} />
                        }
                        const d = day as CalendarDayView
                        const isUnavailable = d.isPast || d.isOutsideWindow
                        return (
                          <button
                            key={d.key}
                            type="button"
                            onClick={() => !isUnavailable && f.handleDateSelect(d.date)}
                            disabled={isUnavailable}
                            className={`${classes.calendarDay} ${d.isSelected ? classes.calendarDaySelected : ''} ${
                              !d.isCurrentMonth ? classes.calendarDayOtherMonth : ''
                            } ${isUnavailable ? classes.calendarDayDisabled : ''}`}
                          >
                            <Typography variant="body">{format(d.date, 'd')}</Typography>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={classes.formSection}>
              <Typography variant="body" className={classes.label}>
                Available Dates
              </Typography>
              <Typography variant="body" className={classes.placeholderText}>
                Please select an appointment type to view available dates
              </Typography>
            </div>
          )}
        </div>

        {!f.isCalendarRefused && (
          <div className={`${classes.rightPanel} ${f.hasSelectedDate ? classes.rightPanelFilled : ''}`}>
            {f.hasSelectedDate && f.selectedDate ? (
              <>
                <div className={classes.panelHeader}>
                  <Icon name="clock" className={classes.panelIcon} />
                  <Typography variant="h2" className={classes.panelTitle}>
                    Select a Time
                  </Typography>
                </div>
                <Typography variant="body" className={classes.panelDescription}>
                  Available times for {f.selectedAppointmentTypeLabel} on {format(f.selectedDate, 'EEEE, MMM d')}
                </Typography>

                <div className={classes.formSection}>
                  <div className={classes.timezoneLabelRow}>
                    <Typography variant="body" className={classes.label}>
                      Timezone
                    </Typography>
                    <button type="button" className={classes.timezoneLink} onClick={f.handleUseBrowserTimezone}>
                      use browser timezone
                    </button>
                  </div>
                  <SelectField
                    value={f.timezoneOptions.find((tz) => tz.value === f.timezone)}
                    onChange={(option: { value?: string }) =>
                      f.setTimezone(option?.value ?? f.timezoneOptions[0]?.value ?? 'UTC')
                    }
                    options={f.timezoneOptions}
                    closeMenuOnSelect={true}
                  />
                </div>

                <div className={classes.formSection}>
                  <Typography variant="body" className={classes.label}>
                    Available Time Slots
                  </Typography>
                  {f.isLoadingSlots ? (
                    <Typography variant="body">Loading available slots...</Typography>
                  ) : f.slotsError ? (
                    <BookingAvailabilityError
                      message={f.slotsErrorMessage}
                      onRetry={f.handleRetrySlots}
                      isRetrying={f.isRetryingSlots}
                    />
                  ) : f.availableTimeSlots.length === 0 ? (
                    <Typography variant="body">No slots available for this date.</Typography>
                  ) : (
                    <>
                      {f.slotAvailabilityError && (
                        <Typography
                          variant="body"
                          style={{
                            color: 'red',
                            marginBottom: '12px',
                            padding: '8px',
                            backgroundColor: '#fff5f5',
                            borderRadius: '4px',
                          }}
                        >
                          {f.slotAvailabilityError}
                        </Typography>
                      )}
                      <div className={classes.timeSlots}>
                        {f.availableTimeSlots.map((slot) => (
                          <button
                            key={slot.startTimeUtc}
                            type="button"
                            disabled={!slot.isFree}
                            onClick={() => slot.isFree && f.handleTimeSelect(slot.time)}
                            className={`${classes.timeSlot} ${
                              f.selectedTime === slot.time && slot.isFree ? classes.timeSlotSelected : ''
                            } ${!slot.isFree ? classes.timeSlotDisabled : ''}`}
                          >
                            <Typography variant="body">{slot.time}</Typography>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {f.selectedTime && (
                  <div className={classes.selectedSummary}>
                    <Typography variant="body">
                      {f.selectedAppointmentTypeLabel} at {f.selectedTime}
                    </Typography>
                  </div>
                )}

                <div className={classes.continueButton}>
                  <Button
                    onClick={f.handleContinue}
                    disabled={!f.selectedTime || f.isCheckingAvailability}
                    isLoading={f.isCheckingAvailability}
                  >
                    {f.continueButtonLabel}
                  </Button>
                </div>
              </>
            ) : (
              <div className={classes.placeholder}>
                <Icon name="clock" className={classes.placeholderIcon} />
                <Typography variant="body" className={classes.placeholderText}>
                  Select a Date
                </Typography>
                <Typography variant="body" className={classes.placeholderSubtext}>
                  Choose a date to see available time slots
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
